import Search from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { SubmitEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api/ApplicationApi';
import { BaseDialog } from 'library/components/controls/dialogs/BaseDialog';
import CustomSeverityDialog from 'library/components/controls/dialogs/CustomSeverityDialog';

import { mapServiceContractDtoToServiceContract } from './mappers';
import type { ServiceContract } from './types';

export type SearchServiceContractDialogProps = {
	onClose: () => void;
	onConfirm: (contract: ServiceContract) => void;
};

export const SearchServiceContractDialog = ({
	onClose,
	onConfirm,
}: SearchServiceContractDialogProps) => {
	const { api } = useProductCenterApi();
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation(['serviceContracts', 'commonButton']);
	const [searchValue, setSearchValue] = useState('');
	const [isCanceling, setIsCanceling] = useState(false);
	const trimmedValue = searchValue.trim();

	const handleSearch = async (event: SubmitEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (trimmedValue === '') {
			return;
		}
		const { data } = await api.getServiceContractInfo(trimmedValue);
		if (!data) {
			enqueueSnackbar(t('serviceContracts:service-contract-not-found'), {
				variant: 'error',
			});
			return;
		}

		onConfirm(mapServiceContractDtoToServiceContract(data));
		onClose();
	};

	const handleOnCancel = useCallback(() => {
		setIsCanceling(true);
	}, []);

	const handleOnAccept = useCallback(() => {
		setIsCanceling(false);
		onClose();
	}, [onClose]);

	const handleOnDecline = useCallback(() => {
		setIsCanceling(false);
	}, []);

	return (
		<>
			<BaseDialog
				title={t('serviceContracts:connect-service-contract')}
				onClose={onClose}>
				<Box component="form" onSubmit={handleSearch} sx={{ mt: 2 }}>
					<TextField
						// autofocus is a usability improvement for search dialogs
						// eslint-disable-next-line jsx-a11y/no-autofocus
						autoFocus
						fullWidth
						variant="standard"
						placeholder={t('serviceContracts:service-contract-id')}
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											type="submit"
											aria-label={t(
												'commonButton:search'
											)}
											disabled={trimmedValue === ''}>
											<Search />
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
				</Box>
				<Divider sx={{ mt: 4, mx: -3 }} />
				<Box
					sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
					<Button
						variant="text"
						color="secondary"
						onClick={handleOnCancel}>
						{t('commonButton:cancel')}
					</Button>
				</Box>
			</BaseDialog>
			<CustomSeverityDialog
				headerText={t('serviceContracts:adding-cancel-dialog-title')}
				content={t('serviceContracts:adding-cancel-dialog-description')}
				onDecline={handleOnDecline}
				onAccept={handleOnAccept}
				acceptButtonLabel={t('commonButton:yes')}
				declineButtonLabel={t('commonButton:no')}
				isOpen={isCanceling}
			/>
		</>
	);
};
